/**
 * SuperBrain Referral Leaderboard Widget
 * Growth loop: shows the top referrers + the current user's rank, so users
 * see there's a real competition and their own progress toward the reward.
 *
 * Privacy: displays only the first letter of each referrer's email as
 * "j***", so no full addresses are ever exposed. The backend enforces this
 * masking — the frontend just renders whatever comes back.
 *
 * Owner Rule #7 (no fake numbers): the whole widget hides until there are
 * at least 3 real referrers with rewarded conversions. Until then, users
 * see a "Be one of the first" placeholder that invites participation
 * without lying about traction.
 *
 * © 2026 A-1 Affiliate Marketing LLC - All Rights Reserved
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const medal = (rank) => {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
};

const ReferralLeaderboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/referrals/leaderboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(r.data);
      } catch (e) {
        setError('leaderboard_unavailable');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) return null;
  if (error || !data) return null;

  // Pre-launch state: honest, no fake numbers
  if (!data.show_leaderboard) {
    return (
      <div
        data-testid="referral-leaderboard-placeholder"
        style={{
          marginTop: '1rem',
          padding: '1rem',
          borderRadius: '10px',
          background: 'rgba(255,255,255,0.03)',
          border: '1px dashed rgba(255,255,255,0.15)',
          color: 'rgba(255,255,255,0.75)',
          fontSize: '0.9rem',
          lineHeight: 1.5,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: '0.35rem', color: '#fff' }}>
          🏆 Referral Leaderboard
        </div>
        <div>
          Coming soon. When 3 members have earned their first reward, the top-referrer board
          goes live — and you could be on it. Share your link and be one of the first.
        </div>
        {data.your_referrals > 0 && (
          <div style={{ marginTop: '0.5rem', color: '#82f4b1', fontWeight: 500 }}>
            You&apos;ve already earned {data.your_referrals} reward
            {data.your_referrals === 1 ? '' : 's'} — nice work.
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="referral-leaderboard"
      style={{
        marginTop: '1rem',
        padding: '1rem',
        borderRadius: '10px',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <div
        style={{
          fontWeight: 700,
          fontSize: '1rem',
          color: '#fff',
          marginBottom: '0.75rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>🏆 Referral Leaderboard</span>
        <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'rgba(255,255,255,0.55)' }}>
          Top 10
        </span>
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {data.top.map((row) => (
          <li
            key={`rank-${row.rank}`}
            data-testid={`leaderboard-row-${row.rank}`}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.5rem 0.6rem',
              borderRadius: '6px',
              marginBottom: '0.25rem',
              background: row.is_you ? 'rgba(130, 244, 177, 0.12)' : 'transparent',
              border: row.is_you ? '1px solid rgba(130, 244, 177, 0.4)' : '1px solid transparent',
              fontSize: '0.92rem',
              color: '#fff',
            }}
          >
            <span style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
              <span style={{ minWidth: '2rem', fontWeight: 600 }}>{medal(row.rank)}</span>
              <span>{row.display}{row.is_you && ' (you)'}</span>
            </span>
            <span style={{ fontWeight: 600, color: row.is_you ? '#82f4b1' : '#fff' }}>
              {row.referrals} paid
            </span>
          </li>
        ))}
      </ul>

      {/* Caller's own line if they're not in the top 10 */}
      {data.your_rank && data.your_rank > 10 && (
        <div
          data-testid="leaderboard-your-rank"
          style={{
            marginTop: '0.5rem',
            padding: '0.5rem 0.6rem',
            borderRadius: '6px',
            background: 'rgba(130, 244, 177, 0.08)',
            fontSize: '0.9rem',
            color: '#82f4b1',
          }}
        >
          Your rank: #{data.your_rank} · {data.your_referrals} paid referral
          {data.your_referrals === 1 ? '' : 's'}
        </div>
      )}

      {(!data.your_rank || data.your_referrals === 0) && (
        <div
          data-testid="leaderboard-your-rank"
          style={{
            marginTop: '0.5rem',
            padding: '0.5rem 0.6rem',
            fontSize: '0.85rem',
            color: 'rgba(255,255,255,0.7)',
          }}
        >
          Share your referral link to make the board.
        </div>
      )}
    </div>
  );
};

export default ReferralLeaderboard;
